import { ApiError, NetworkError } from '../../lib/api';
import type { AuthedRequest } from '../auth/AuthProvider';

/** Supported mosaics: 1, 2×2, 3×3 and 4×4 tiles. */
export type Grid = 1 | 4 | 9 | 16;
export const grids: Grid[] = [1, 4, 9, 16];
export const gridLabels: Record<Grid, string> = { 1: '1', 4: '2×2', 9: '3×3', 16: '4×4' };
export const MAX_TILES = 16;

export interface LayoutTile {
  position: number;
  device_id: string;
}

/** Mirrors /api/v1/layouts. */
export interface Layout {
  id: string;
  name: string;
  grid: Grid;
  shared: boolean;
  owner: { id: string; display_name: string };
  /** Whether the current user may change or delete it. */
  editable: boolean;
  tiles: LayoutTile[];
  created_at: string;
  updated_at: string;
}

export interface LayoutBody {
  name?: string;
  grid?: Grid;
  shared?: boolean;
  tiles?: LayoutTile[];
}

export const layoutsKey = ['layouts'] as const;

export async function listLayouts(req: AuthedRequest): Promise<Layout[]> {
  return (await req<{ items: Layout[] }>('GET', '/api/v1/layouts')).items;
}
export const layoutKey = (id: string) => ['layouts', id] as const;
export const getLayout = (req: AuthedRequest, id: string) =>
  req<Layout>('GET', `/api/v1/layouts/${encodeURIComponent(id)}`);
export const createLayout = (req: AuthedRequest, b: LayoutBody) =>
  req<Layout>('POST', '/api/v1/layouts', b);
export const updateLayout = (req: AuthedRequest, id: string, b: LayoutBody) =>
  req<Layout>('PATCH', `/api/v1/layouts/${encodeURIComponent(id)}`, b);
export const deleteLayout = (req: AuthedRequest, id: string) =>
  req<null>('DELETE', `/api/v1/layouts/${encodeURIComponent(id)}`);

/** Occupied positions → device ids, as sent to the API. */
export function toTiles(slots: (string | null)[], grid: Grid): LayoutTile[] {
  const out: LayoutTile[] = [];
  slots.slice(0, grid).forEach((id, position) => {
    if (id) out.push({ position, device_id: id });
  });
  return out;
}

/** API tiles → one slot per position (null = empty), always MAX_TILES long. */
export function toSlots(tiles: LayoutTile[]): (string | null)[] {
  const slots: (string | null)[] = Array.from({ length: MAX_TILES }, () => null);
  for (const t of tiles) if (t.position < MAX_TILES) slots[t.position] = t.device_id;
  return slots;
}

/** Operator-facing text for a failed layout save. */
export function layoutErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const f = err.fields;
    if (f.name === 'taken') return 'Você já tem um layout com este nome.';
    if (f.name === 'required') return 'Informe um nome.';
    if (f.name === 'too_long') return 'Nome longo demais (máximo 120 caracteres).';
    if (f.tiles === 'unknown') return 'Uma das câmeras do layout não existe mais.';
    if (f.tiles) return 'As câmeras não cabem na grade escolhida.';
    if (err.status === 403) return 'Você não tem permissão para alterar este layout.';
    if (err.status === 404) return 'Este layout não existe mais.';
  }
  if (err instanceof NetworkError) return 'Não foi possível contatar o servidor.';
  return 'Não foi possível salvar o layout.';
}
